import { type ComponentType, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Download,
  Gauge,
  Loader2,
  Minus,
  RefreshCw,
  Server,
  Table2,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useAuth } from '@edusense/shared-auth-core';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { hasAdminAuthorizationHeader, refreshAdminAccessToken } from '../auth/adminAccessToken';
import { buildRequestCacheKey, fetchWithTtlCache } from '../lib/requestCache';
import { useAdminApplicationScope } from '../apps/AdminApplicationScope';
import {
  FUNNEL_METRIC_TYPE_OPTIONS,
  buildAdFunnelRows,
  buildAnonymousDeviceFunnelRows,
  buildDailyMetricRows,
  buildDwellWeeklySummary,
  buildPlatformDailyMetricRows,
  buildFeatureEngagementRows,
  buildSajugMonitoringCards,
  buildSajugTrafficChartRows,
  buildSajugTrafficDailyRows,
  buildSajugTrafficSummaryCards,
  buildFunnelImportantIssues,
  buildFunnelRows,
  formatMetricPaymentPlatform,
  formatMetricTags,
  type AdFunnelSummaryResponse,
  type DailyActiveUserPoint,
  type DailyActiveUsersResponse,
  type FunnelImportantIssue,
  type FunnelSummaryResponse,
  type PlatformDailyMetricRow,
  type DailyMetricCell,
  type DailyMetricRow,
  type DwellWeeklySummary,
  type FeatureEngagementRow,
  type FunnelRow,
  type MetricTags,
  type MetricsSummary,
  type PageDwellTimeSummaryItem,
  type PageDwellTimeSummaryResponse,
  type SajugMonitoringCard,
  type SajugMonitoringSnapshot,
  type SajugErrorHistoryResponse,
  type SajugRouteDailyError,
  type SajugTrafficChartRow,
  type SajugTrafficDailyRow,
  type SajugTrafficHistoryResponse,
} from '../types/metrics';
import { ensureAdminRequestHeaders } from '../auth/adminRequestHeaders';
import { AdFunnelBreakdownTable, AdFunnelPlatformPanel, FunnelIssueRow, formatAdFunnelStepLabel } from './metrics/AdFunnelPanels';
import { BusinessMetricsSection } from './metrics/BusinessMetricsSection';
import { formatMetricNumber, formatMetricPercent } from './metrics/metricFormat';

type MetricItem = {
  id?: number | string;
  userId?: string;
  type?: string;
  name?: string;
  value?: number;
  tags?: MetricTags;
  timestamp?: string;
};

type AggregatedMetricsResponse = {
  data?: Record<string, Record<string, number>>;
};

type MetricsSummaryResponse = MetricsSummary;

type DailyMetricsView = 'chart' | 'excel';
type DailyActiveUsersView = 'chart' | 'table';
type PlatformDailyMetricsView = 'all' | 'web' | 'mobile' | 'unknown';
type SajugLoadView = 'summary' | 'daily' | 'hourly';
type DetailMetricsView = 'daily' | 'raw';
type TrafficMetricKind = 'users' | 'requests' | 'errors' | 'percent' | 'latency' | 'activeRequests';
type MetricsViewModeOption<T extends string> = {
  value: T;
  label: string;
  icon: ComponentType<{ className?: string }>;
};

const METRIC_TYPE_OPTIONS = [
  { value: 'ALL', label: '모든 메트릭' },
  { value: 'PAYMENT_PROCESSED', label: '결제 처리' },
  { value: 'PAYMENT_FAILED', label: '결제 실패' },
  { value: 'SUBSCRIPTION_CREATED', label: '구독 생성' },
  { value: 'SUBSCRIPTION_CANCELLED', label: '구독 취소' },
  { value: 'REVENUE', label: '매출' },
  ...FUNNEL_METRIC_TYPE_OPTIONS,
];

const GROUP_BY_OPTIONS = [
  { value: 'HOUR', label: '시간별' },
  { value: 'DAY', label: '일별' },
  { value: 'WEEK', label: '주별' },
  { value: 'MONTH', label: '월별' },
];

const EXPORT_FORMAT_OPTIONS = [
  { value: 'CSV', label: 'CSV' },
  { value: 'JSON', label: 'JSON' },
  { value: 'PDF', label: 'PDF' },
];

const MONITORING_CARD_ICONS = [Users, Activity, Server, Gauge, AlertTriangle, Gauge];
const TRAFFIC_CARD_ICONS = [Activity, Users, Server, Gauge];
const CORE_METRICS_TIMEOUT_MS = 8000;
// daily-by-platform은 5개 집계 중 가장 무거워(전체 주문 스캔+태그 조인) 별도로 더 넉넉한 타임아웃 사용
const PLATFORM_METRICS_TIMEOUT_MS = 15000;
const OPTIONAL_METRICS_TIMEOUT_MS = 6000;
const CORE_METRICS_CACHE_TTL_MS = 30_000;
const OPTIONAL_METRICS_CACHE_TTL_MS = 45_000;
const SAJUG_MONITORING_CACHE_TTL_MS = 10_000;
const SAJUG_TRAFFIC_CACHE_TTL_MS = 60_000;

const monitoringToneClass: Record<SajugMonitoringCard['tone'], string> = {
  normal: 'border-border bg-background',
  watch: 'border-amber-300 bg-amber-50/70 text-amber-950',
  saturated: 'border-destructive/50 bg-destructive/10 text-destructive',
};

const settledData = <T,>(result: PromiseSettledResult<{ data: T }>, label: string): T | null => {
  if (result.status === 'fulfilled') {
    return result.value.data;
  }
  console.warn(`Failed to fetch ${label}:`, result.reason);
  return null;
};

const isUnauthorizedError = (error: unknown) => (
  axios.isAxiosError(error) && error.response?.status === 401
);

// 타임아웃/네트워크 오류(응답 없음)는 일시적일 수 있어 1회 재시도 대상으로 본다.
// daily-by-platform 같은 무거운 집계가 간헐적으로 8s를 넘겨 섹션이 사라지던 버그 방지.
const isRetriableNetworkError = (error: unknown) => (
  axios.isAxiosError(error) && !error.response &&
  (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || error.code === 'ERR_NETWORK')
);

const formatMetricCurrency = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '₩0';
  }
  return value.toLocaleString('ko-KR', {
    style: 'currency',
    currency: 'KRW',
    maximumFractionDigits: 0,
  });
};

const formatDurationSeconds = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return '0초';
  }
  const rounded = Math.round(value);
  if (rounded < 60) {
    return `${rounded}초`;
  }
  const minutes = Math.floor(rounded / 60);
  const seconds = rounded % 60;
  if (minutes < 60) {
    return seconds > 0 ? `${minutes}분 ${seconds}초` : `${minutes}분`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}시간 ${remainingMinutes}분` : `${hours}시간`;
};

const formatSignedMetricNumber = (value: number, suffix = '') => {
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatMetricNumber(value)}${suffix}`;
};

const formatSignedMetricCurrency = (value: number) => {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${formatMetricCurrency(Math.abs(value))}`;
};

const formatSignedDurationSeconds = (value: number) => {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${formatDurationSeconds(Math.abs(value))}`;
};

const formatDailyMetricValue = (cell: DailyMetricCell, kind: 'count' | 'currency' | 'duration') => {
  if (kind === 'currency') {
    return formatMetricCurrency(cell.value);
  }
  if (kind === 'duration') {
    return formatDurationSeconds(cell.value);
  }
  return `${formatMetricNumber(cell.value)}회`;
};

const formatDailyMetricChange = (cell: DailyMetricCell, kind: 'count' | 'currency' | 'duration') => {
  if (cell.changePercent === null) {
    return '기준일';
  }
  const value = kind === 'currency'
    ? formatSignedMetricCurrency(cell.change)
    : kind === 'duration'
      ? formatSignedDurationSeconds(cell.change)
      : formatSignedMetricNumber(cell.change, '회');
  return `${value} · ${formatSignedMetricNumber(cell.changePercent, '%')}`;
};

const formatTrafficMetricValue = (value: number, kind: TrafficMetricKind) => {
  if (kind === 'percent') {
    return `${formatMetricNumber(value)}%`;
  }
  if (kind === 'latency') {
    return `${formatMetricNumber(Math.round(value))}ms`;
  }
  if (kind === 'users') {
    return `${formatMetricNumber(value)}명`;
  }
  if (kind === 'activeRequests') {
    return `${formatMetricNumber(value)}개`;
  }
  return `${formatMetricNumber(value)}회`;
};

const formatSignedTrafficMetricValue = (value: number, kind: TrafficMetricKind) => {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  const absValue = Math.abs(value);
  if (kind === 'percent') {
    return `${sign}${formatMetricNumber(absValue)}%p`;
  }
  return `${sign}${formatTrafficMetricValue(absValue, kind)}`;
};

const formatTrafficMetricChange = (cell: DailyMetricCell, kind: TrafficMetricKind) => {
  if (cell.changePercent === null) {
    return '기준일';
  }
  return `${formatSignedTrafficMetricValue(cell.change, kind)} · ${formatSignedMetricNumber(cell.changePercent, '%')}`;
};

const formatMetricTimestamp = (value?: string) => {
  if (!value) {
    return '-';
  }
  return new Date(value).toLocaleString('ko-KR');
};

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const dateInputUtcTimestamp = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return Date.UTC(year || 1970, Math.max(0, (month || 1) - 1), day || 1);
};

const inclusiveDateSpanDays = (start: string, end: string) => (
  Math.max(1, Math.round((dateInputUtcTimestamp(end) - dateInputUtcTimestamp(start)) / 86_400_000) + 1)
);

export default function MetricsPage() {
  const { token: accessToken } = useAuth();
  const { application, applicationId } = useAdminApplicationScope();
  const isSajugApplication = applicationId === 'sajug';
  const hasDailyActiveUsers = isSajugApplication || application.capabilities.productMetrics;
  const requestIdRef = useRef(0);
  const [startDate, setStartDate] = useState(
    toDateInputValue(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
  );
  const [endDate, setEndDate] = useState(toDateInputValue(new Date()));
  const [metricType, setMetricType] = useState('ALL');
  const [groupBy, setGroupBy] = useState('DAY');
  const [exportFormat, setExportFormat] = useState('CSV');
  const [dailyMetricsView, setDailyMetricsView] = useState<DailyMetricsView>('chart');
  const [dailyActiveUsersView, setDailyActiveUsersView] = useState<DailyActiveUsersView>('chart');
  const [platformDailyMetricsView, setPlatformDailyMetricsView] = useState<PlatformDailyMetricsView>('all');
  const [sajugLoadView, setSajugLoadView] = useState<SajugLoadView>('daily');
  const [detailMetricsView, setDetailMetricsView] = useState<DetailMetricsView>('daily');
  const [metrics, setMetrics] = useState<MetricItem[]>([]);
  const [aggregatedMetrics, setAggregatedMetrics] = useState<AggregatedMetricsResponse | null>(null);
  const [platformAggregatedMetrics, setPlatformAggregatedMetrics] = useState<{
    data?: Record<string, Record<string, Record<string, number>>>;
  } | null>(null);
  const [adFunnelSummary, setAdFunnelSummary] = useState<AdFunnelSummaryResponse | null>(null);
  const [adFunnelError, setAdFunnelError] = useState<string | null>(null);
  const [platformAggregatedError, setPlatformAggregatedError] = useState<string | null>(null);
  const [summary, setSummary] = useState<MetricsSummaryResponse | null>(null);
  const [funnelSummary, setFunnelSummary] = useState<FunnelSummaryResponse | null>(null);
  const [dailyActiveUsers, setDailyActiveUsers] = useState<DailyActiveUsersResponse | null>(null);
  const [dailyActiveUsersError, setDailyActiveUsersError] = useState<string | null>(null);
  const [pageDwellSummary, setPageDwellSummary] = useState<PageDwellTimeSummaryResponse | null>(null);
  const [sajugMonitoring, setSajugMonitoring] = useState<SajugMonitoringSnapshot | null>(null);
  const [sajugTraffic, setSajugTraffic] = useState<SajugTrafficHistoryResponse | null>(null);
  const [sajugDailyTraffic, setSajugDailyTraffic] = useState<SajugTrafficHistoryResponse | null>(null);
  const [sajugErrors, setSajugErrors] = useState<SajugErrorHistoryResponse | null>(null);
  const [sajugMonitoringError, setSajugMonitoringError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailMetricsLoading, setDetailMetricsLoading] = useState(false);
  const [pageDwellLoading, setPageDwellLoading] = useState(false);
  const [sajugLoading, setSajugLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ensureAuthHeaders = useCallback(() => ensureAdminRequestHeaders(accessToken), [accessToken]);

  const getWithAuthRetry = useCallback(async <T,>(
    url: string,
    config: {
      params?: Record<string, unknown>;
      timeout?: number;
      headers?: Record<string, string>;
    } = {}
  ) => {
    const headers = await ensureAuthHeaders();
    if (!hasAdminAuthorizationHeader(headers)) {
      throw new Error('Admin authorization token is missing');
    }

    const requestConfig = {
      ...config,
      headers: { ...headers, ...(config.headers ?? {}) },
      withCredentials: true,
    };

    try {
      return await axios.get<T>(url, requestConfig);
    } catch (requestError) {
      // 일시적 타임아웃/네트워크 오류는 즉시 1회 재시도 (간헐 실패로 섹션이 숨겨지는 문제 방지)
      if (isRetriableNetworkError(requestError)) {
        return await axios.get<T>(url, requestConfig);
      }
      if (!isUnauthorizedError(requestError)) {
        throw requestError;
      }

      const refreshedToken = await refreshAdminAccessToken();
      if (!refreshedToken) {
        throw requestError;
      }

      return axios.get<T>(url, {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          Authorization: `Bearer ${refreshedToken}`,
        },
      });
    }
  }, [ensureAuthHeaders]);

  const getCachedWithAuth = useCallback(async <T,>(
    url: string,
    config: {
      params?: Record<string, unknown>;
      timeout?: number;
      headers?: Record<string, string>;
    },
    ttlMs: number
  ): Promise<{ data: T }> => {
    const cacheKey = buildRequestCacheKey(url, config.params);
    const data = await fetchWithTtlCache(cacheKey, ttlMs, async () => {
      const response = await getWithAuthRetry<T>(url, config);
      return response.data;
    });
    return { data };
  }, [getWithAuthRetry]);

  const postWithAuthRetry = useCallback(async <T,>(
    url: string,
    data: unknown,
    config: {
      responseType?: 'blob';
      headers?: Record<string, string>;
    } = {}
  ) => {
    const headers = await ensureAuthHeaders();
    if (!hasAdminAuthorizationHeader(headers)) {
      throw new Error('Admin authorization token is missing');
    }

    const requestConfig = {
      ...config,
      headers: { ...headers, ...(config.headers ?? {}) },
      withCredentials: true,
    };

    try {
      return await axios.post<T>(url, data, requestConfig);
    } catch (requestError) {
      if (!isUnauthorizedError(requestError)) {
        throw requestError;
      }

      const refreshedToken = await refreshAdminAccessToken();
      if (!refreshedToken) {
        throw requestError;
      }

      return axios.post<T>(url, data, {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          Authorization: `Bearer ${refreshedToken}`,
        },
      });
    }
  }, [ensureAuthHeaders]);

  const queryParams = useMemo(
    () => ({
      startDate: `${startDate}T00:00:00`,
      endDate: `${endDate}T23:59:59`,
      type: metricType === 'ALL' ? undefined : metricType,
      groupBy,
      limit: 100,
      applicationId,
    }),
    [applicationId, endDate, groupBy, metricType, startDate]
  );

  const loadMetrics = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const isCurrentRequest = () => requestIdRef.current === requestId;
    const shouldLoadRawMetrics = detailMetricsView === 'raw';
    setLoading(true);
    setDetailMetricsLoading(shouldLoadRawMetrics);
    setPageDwellLoading(isSajugApplication);
    setSajugLoading(isSajugApplication);
    setError(null);
    setDailyActiveUsersError(null);
    setSajugMonitoringError(null);

    try {
      const headers = await ensureAuthHeaders();
      if (!hasAdminAuthorizationHeader(headers)) {
        if (isCurrentRequest()) {
          setDetailMetricsLoading(false);
          setPageDwellLoading(false);
          setSajugLoading(false);
          setLoading(false);
          setError('관리자 세션이 만료되었습니다. 다시 로그인해주세요.');
        }
        return;
      }

      if (!shouldLoadRawMetrics) {
        setMetrics([]);
        setDetailMetricsLoading(false);
      }

      if (isSajugApplication) {
        void Promise.allSettled([
        getCachedWithAuth<SajugMonitoringSnapshot>('/api/admin/monitoring/sajug', {
          timeout: OPTIONAL_METRICS_TIMEOUT_MS,
        }, SAJUG_MONITORING_CACHE_TTL_MS),
        getCachedWithAuth<SajugTrafficHistoryResponse>('/api/admin/monitoring/sajug/traffic', {
          params: {
            hours: 24,
            bucketMinutes: 60,
          },
          timeout: OPTIONAL_METRICS_TIMEOUT_MS,
        }, SAJUG_TRAFFIC_CACHE_TTL_MS),
        getCachedWithAuth<SajugTrafficHistoryResponse>('/api/admin/monitoring/sajug/traffic', {
          params: {
            hours: 24 * 30,
            bucketMinutes: 60,
          },
          timeout: OPTIONAL_METRICS_TIMEOUT_MS,
        }, SAJUG_TRAFFIC_CACHE_TTL_MS),
        getCachedWithAuth<SajugErrorHistoryResponse>('/api/admin/monitoring/sajug/errors', {
          params: {
            days: 30,
            limit: 100,
          },
          timeout: OPTIONAL_METRICS_TIMEOUT_MS,
        }, SAJUG_TRAFFIC_CACHE_TTL_MS),
        ]).then(([monitoringResult, trafficResult, dailyTrafficResult, errorsResult]) => {
        if (!isCurrentRequest()) {
          return;
        }

        const nextMonitoring = settledData(monitoringResult, 'Sajug monitoring snapshot');
        const nextTraffic = settledData(trafficResult, 'Sajug traffic history');
        const nextDailyTraffic = settledData(dailyTrafficResult, 'Sajug daily traffic history');
        const nextErrors = settledData(errorsResult, 'Sajug error history');

        setSajugMonitoring(nextMonitoring);
        setSajugTraffic(nextTraffic);
        setSajugDailyTraffic(nextDailyTraffic ?? nextTraffic);
        setSajugErrors(nextErrors);
        setSajugMonitoringError(
          nextMonitoring || nextTraffic || nextDailyTraffic || nextErrors
            ? null
            : '사주 백엔드 부하 데이터를 불러오지 못했습니다.'
        );
        }).finally(() => {
          if (isCurrentRequest()) {
            setSajugLoading(false);
          }
        });
      } else if (isCurrentRequest()) {
        setSajugMonitoring(null);
        setSajugTraffic(null);
        setSajugDailyTraffic(null);
        setSajugErrors(null);
        setSajugMonitoringError(null);
        setSajugLoading(false);
      }

      const skippedProductRequest = <T,>() => Promise.resolve<PromiseSettledResult<{ data: T }> | null>(null);
      const settledProductRequest = <T,>(request: Promise<{ data: T }>) => request.then(
        (response): PromiseSettledResult<{ data: T }> => ({ status: 'fulfilled', value: response }),
        (reason): PromiseSettledResult<{ data: T }> => ({ status: 'rejected', reason })
      );

      // 결제 메트릭은 모든 앱에서 applicationId로 분리하고, 사주 전용 상품 퍼널은 사주앱에서만 호출한다.
      const [
        funnelResponse,
        aggregatedResponse,
        platformAggregatedResponse,
        adFunnelResponse,
        summaryResponse,
        dailyActiveUsersResponse,
      ] = await Promise.all([
        isSajugApplication
          ? settledProductRequest(getCachedWithAuth<FunnelSummaryResponse>('/api/v1/product-metrics/funnel', {
            params: {
              startDate: queryParams.startDate,
              endDate: queryParams.endDate,
            },
            timeout: CORE_METRICS_TIMEOUT_MS,
          }, CORE_METRICS_CACHE_TTL_MS))
          : skippedProductRequest<FunnelSummaryResponse>(),
        settledProductRequest(getCachedWithAuth<AggregatedMetricsResponse>('/api/v1/metrics/aggregated', {
          params: {
            startDate: queryParams.startDate,
            endDate: queryParams.endDate,
            applicationId: queryParams.applicationId,
          },
          timeout: CORE_METRICS_TIMEOUT_MS,
        }, CORE_METRICS_CACHE_TTL_MS)),
        isSajugApplication
          ? settledProductRequest(getCachedWithAuth<{
            data?: Record<string, Record<string, Record<string, number>>>;
          }>('/api/v1/product-metrics/aggregated/daily-by-platform', {
            params: {
              startDate: queryParams.startDate,
              endDate: queryParams.endDate,
            },
            timeout: PLATFORM_METRICS_TIMEOUT_MS,
          }, CORE_METRICS_CACHE_TTL_MS))
          : skippedProductRequest<{ data?: Record<string, Record<string, Record<string, number>>> }>(),
        isSajugApplication
          ? settledProductRequest(getCachedWithAuth<AdFunnelSummaryResponse>('/api/v1/product-metrics/ad-funnel', {
            params: {
              startDate: queryParams.startDate,
              endDate: queryParams.endDate,
            },
            timeout: CORE_METRICS_TIMEOUT_MS,
          }, CORE_METRICS_CACHE_TTL_MS))
          : skippedProductRequest<AdFunnelSummaryResponse>(),
        settledProductRequest(getCachedWithAuth<MetricsSummaryResponse>('/api/v1/metrics/summary', {
          params: {
            startDate: queryParams.startDate,
            endDate: queryParams.endDate,
            applicationId: queryParams.applicationId,
          },
          timeout: CORE_METRICS_TIMEOUT_MS,
        }, CORE_METRICS_CACHE_TTL_MS)),
        isSajugApplication
          ? settledProductRequest(getCachedWithAuth<DailyActiveUsersResponse>('/api/v1/product-metrics/active-users/daily', {
            params: {
              startDate: queryParams.startDate,
              endDate: queryParams.endDate,
            },
            timeout: CORE_METRICS_TIMEOUT_MS,
          }, CORE_METRICS_CACHE_TTL_MS))
          : application.capabilities.productMetrics
            ? settledProductRequest(getCachedWithAuth<DailyActiveUsersResponse>('/api/v1/metrics/active-users/daily', {
              params: {
                startDate: queryParams.startDate,
                endDate: queryParams.endDate,
                applicationId: queryParams.applicationId,
              },
              timeout: CORE_METRICS_TIMEOUT_MS,
            }, CORE_METRICS_CACHE_TTL_MS))
            : skippedProductRequest<DailyActiveUsersResponse>(),
      ]);

      if (!isCurrentRequest()) {
        return;
      }

      const nextFunnel = funnelResponse ? settledData(funnelResponse, 'funnel metrics summary') : null;
      const nextAggregated = aggregatedResponse ? settledData(aggregatedResponse, 'aggregated metrics') : null;
      const nextPlatformAggregated = platformAggregatedResponse?.status === 'fulfilled'
        ? platformAggregatedResponse.value.data
        : null;
      const nextAdFunnel = adFunnelResponse?.status === 'fulfilled'
        ? adFunnelResponse.value.data
        : null;
      const nextSummary = summaryResponse ? settledData(summaryResponse, 'metrics summary') : null;
      const nextDailyActiveUsers = dailyActiveUsersResponse
        ? settledData(dailyActiveUsersResponse, 'daily active users')
        : null;

      setFunnelSummary(nextFunnel);
      setAggregatedMetrics(nextAggregated);
      setPlatformAggregatedMetrics(nextPlatformAggregated);
      setAdFunnelSummary(nextAdFunnel);
      setPlatformAggregatedError(
        platformAggregatedResponse?.status === 'rejected'
          ? '플랫폼별 일별 메트릭 데이터를 불러오지 못했습니다.'
          : null
      );
      setAdFunnelError(
        adFunnelResponse?.status === 'rejected'
          ? '광고 퍼널 데이터를 불러오지 못했습니다.'
          : null
      );
      setSummary(nextSummary);
      setDailyActiveUsers(nextDailyActiveUsers);
      setDailyActiveUsersError(
        dailyActiveUsersResponse?.status === 'rejected'
          ? '일일 활성 사용자 데이터를 불러오지 못했습니다.'
          : null
      );
      if (!nextAggregated && !nextSummary && !nextFunnel) {
        setError('메트릭 조회에 실패했습니다.');
      }

      if (isSajugApplication) {
        void getCachedWithAuth<PageDwellTimeSummaryResponse>('/api/v1/product-metrics/page-dwell', {
          params: {
            startDate: queryParams.startDate,
            endDate: queryParams.endDate,
          },
          timeout: OPTIONAL_METRICS_TIMEOUT_MS,
        }, OPTIONAL_METRICS_CACHE_TTL_MS).then((response) => {
          if (isCurrentRequest()) {
            setPageDwellSummary(response.data ?? null);
          }
        }).catch((pageDwellError) => {
          console.warn('Failed to fetch page dwell metrics:', pageDwellError);
          if (isCurrentRequest()) {
            setPageDwellSummary(null);
          }
        }).finally(() => {
          if (isCurrentRequest()) {
            setPageDwellLoading(false);
          }
        });
      } else if (isCurrentRequest()) {
        setPageDwellSummary(null);
        setPageDwellLoading(false);
      }

      if (shouldLoadRawMetrics) {
        void getCachedWithAuth<MetricItem[]>('/api/v1/metrics', {
          params: {
            ...queryParams,
            limit: 50,
          },
          timeout: OPTIONAL_METRICS_TIMEOUT_MS,
        }, OPTIONAL_METRICS_CACHE_TTL_MS).then((response) => {
          if (isCurrentRequest()) {
            setMetrics(response.data ?? []);
          }
        }).catch((metricsError) => {
          console.warn('Failed to fetch raw admin metrics:', metricsError);
          if (isCurrentRequest()) {
            setMetrics([]);
          }
        }).finally(() => {
          if (isCurrentRequest()) {
            setDetailMetricsLoading(false);
          }
        });
      }
    } catch (fetchError) {
      console.error('Failed to fetch admin metrics:', fetchError);
      if (isCurrentRequest()) {
        setAggregatedMetrics(null);
        setPlatformAggregatedMetrics(null);
        setAdFunnelSummary(null);
        setAdFunnelError(isSajugApplication ? '광고 퍼널 데이터를 불러오지 못했습니다.' : null);
        setSummary(null);
        setFunnelSummary(null);
        setDailyActiveUsers(null);
        setDailyActiveUsersError(hasDailyActiveUsers ? '일일 활성 사용자 데이터를 불러오지 못했습니다.' : null);
        setPageDwellLoading(false);
        setDetailMetricsLoading(false);
        setError('메트릭 조회에 실패했습니다.');
      }
    } finally {
      if (isCurrentRequest()) {
        setLoading(false);
      }
    }
  }, [application.capabilities.productMetrics, detailMetricsView, ensureAuthHeaders, getCachedWithAuth, hasDailyActiveUsers, isSajugApplication, queryParams]);

  useEffect(() => {
    loadMetrics();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadMetrics]);

  const handleExport = async () => {
    setExporting(true);

    try {
      const response = await postWithAuthRetry<Blob>(
        '/api/v1/metrics/export',
        {
          startDate: queryParams.startDate,
          endDate: queryParams.endDate,
          types: metricType === 'ALL' ? [] : [metricType],
          format: exportFormat,
          applicationId,
        },
        {
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], {
        type:
          exportFormat === 'CSV'
            ? 'text/csv'
            : exportFormat === 'JSON'
              ? 'application/json'
              : 'application/pdf',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = `metrics_${new Date().toISOString()}.${exportFormat.toLowerCase()}`;
      document.body.appendChild(anchor);
      anchor.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(anchor);
    } catch (exportError) {
      console.error('Failed to export admin metrics:', exportError);
      setError('메트릭 내보내기에 실패했습니다.');
    } finally {
      setExporting(false);
    }
  };

  const applyDailyActiveUsersRange = (days: number) => {
    const rangeEnd = new Date();
    const rangeStart = new Date(rangeEnd);
    rangeStart.setDate(rangeStart.getDate() - Math.max(0, days - 1));
    setStartDate(toDateInputValue(rangeStart));
    setEndDate(toDateInputValue(rangeEnd));
  };

  const summaryEntries = Object.entries(summary ?? {});
  const funnelRows = buildFunnelRows(funnelSummary?.total ?? summary ?? {});
  const anonymousDeviceFunnelRows = buildAnonymousDeviceFunnelRows(funnelSummary);
  const funnelImportantIssues = buildFunnelImportantIssues(funnelSummary?.total ?? summary ?? {});
  const platformFunnelRows = Object.entries(funnelSummary?.platforms ?? {})
    .filter(([platform]) => platform === 'web' || platform === 'mobile')
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([platform, platformSummary]) => ({
      platform,
      rows: buildFunnelRows(platformSummary),
    }));
  const dailyMetricRows = buildDailyMetricRows(aggregatedMetrics?.data ?? {});
  const platformDailyMetricRows = buildPlatformDailyMetricRows(platformAggregatedMetrics?.data ?? {});
  const adFunnelRows = buildAdFunnelRows(adFunnelSummary?.totals ?? {});
  const webAdFunnelRows = buildAdFunnelRows(adFunnelSummary?.platforms?.['web'] ?? {});
  const mobileAdFunnelRows = buildAdFunnelRows(adFunnelSummary?.platforms?.['mobile'] ?? {});
  const latestDailyMetricRow = dailyMetricRows[dailyMetricRows.length - 1] ?? null;
  const periodDwellEvents = dailyMetricRows.reduce((total, row) => total + row.dwellEvents.value, 0);
  const periodDwellTotalSeconds = dailyMetricRows.reduce((total, row) => total + row.dwellTotalSeconds, 0);
  const periodDwellAverageSeconds = periodDwellEvents > 0 ? periodDwellTotalSeconds / periodDwellEvents : 0;
  const dwellWeeklySummary = buildDwellWeeklySummary(dailyMetricRows);
  const sajugMonitoringCards = sajugMonitoring ? buildSajugMonitoringCards(sajugMonitoring) : [];
  const sajugTrafficCards = buildSajugTrafficSummaryCards(sajugTraffic);
  const sajugTrafficRows = buildSajugTrafficChartRows(sajugTraffic);
  const sajugTrafficDailyRows = buildSajugTrafficDailyRows(sajugDailyTraffic ?? sajugTraffic);
  const topSajugRoutes = (sajugMonitoring?.routes ?? []).slice(0, 6);
  const pageDwellRows = (pageDwellSummary?.items ?? []).slice(0, 10);
  const featureEngagementRows = buildFeatureEngagementRows({
    features: funnelSummary?.features,
    routes: sajugMonitoring?.routes,
    dwellItems: pageDwellSummary?.items,
  }).slice(0, 12);
  const selectedDateSpanDays = inclusiveDateSpanDays(startDate, endDate);
  const pageDwellMaxTotal = Math.max(1, ...pageDwellRows.map((row) => row.totalSeconds || 0));
  const pageDwellPlatformRowsByPath = new Map<string, PageDwellTimeSummaryItem[]>();
  Object.entries(pageDwellSummary?.platforms ?? {}).forEach(([platform, rows]) => {
    rows.forEach((row) => {
      pageDwellPlatformRowsByPath.set(row.path, [
        ...(pageDwellPlatformRowsByPath.get(row.path) ?? []),
        { ...row, platform },
      ]);
    });
  });
  const selectClasses =
    'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold">{application.label} 메트릭</h1>
          <p className="text-muted-foreground">
            {isSajugApplication
              ? '익명/로그인 사용자 퍼널과 결제/구독 메트릭을 조회합니다.'
              : '앱이 명시된 결제·구독 이벤트만 분리해 조회합니다.'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadMetrics} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            새로고침
          </Button>
          <Button onClick={handleExport} disabled={loading || exporting}>
            {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            내보내기
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">시작일</label>
              <input className={selectClasses} type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">종료일</label>
              <input className={selectClasses} type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">메트릭 타입</label>
              <select className={selectClasses} value={metricType} onChange={(event) => setMetricType(event.target.value)}>
                {METRIC_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">집계 단위</label>
              <select className={selectClasses} value={groupBy} onChange={(event) => setGroupBy(event.target.value)}>
                {GROUP_BY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">내보내기 형식</label>
              <select className={selectClasses} value={exportFormat} onChange={(event) => setExportFormat(event.target.value)}>
                {EXPORT_FORMAT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="py-4">
          <div className={`grid grid-cols-1 gap-4 ${isSajugApplication ? 'xl:grid-cols-3' : 'xl:grid-cols-2'}`}>
            <MetricsViewModeControl<DailyMetricsView>
              label="일별 메트릭"
              value={dailyMetricsView}
              onChange={setDailyMetricsView}
              options={[
                { value: 'chart', label: '차트', icon: BarChart3 },
                { value: 'excel', label: '엑셀', icon: Table2 },
              ]}
            />
            {isSajugApplication ? (
              <MetricsViewModeControl<SajugLoadView>
                label="사주 부하"
                value={sajugLoadView}
                onChange={setSajugLoadView}
                options={[
                  { value: 'daily', label: '일별', icon: Table2 },
                  { value: 'summary', label: '요약', icon: Gauge },
                  { value: 'hourly', label: '시간대', icon: BarChart3 },
                ]}
              />
            ) : null}
            <MetricsViewModeControl<DetailMetricsView>
              label="상세 메트릭"
              value={detailMetricsView}
              onChange={setDetailMetricsView}
              options={[
                { value: 'daily', label: '일별', icon: Table2 },
                { value: 'raw', label: '원본', icon: Activity },
              ]}
            />
          </div>
        </CardContent>
      </Card>

      {error ? (
        <Card className="border-destructive">
          <CardContent className="py-4 text-sm text-destructive">{error}</CardContent>
        </Card>
      ) : null}
      {application.capabilities.businessMetrics ? <BusinessMetricsSection applicationId={applicationId} startDate={startDate} endDate={endDate} /> : null}
      {!hasDailyActiveUsers ? (
        <Card className="border-amber-200 bg-amber-50/60">
          <CardContent className="py-4 text-sm text-amber-950">
            {application.label}의 DAU·가입 퍼널·페이지 체류는 아직 공통 관리자 수집기에 연결되지 않았습니다.
            현재 화면은 다른 앱 수치를 대신 보여주지 않고, 앱 ID가 확인된 결제·구독 메트릭만 표시합니다.
          </CardContent>
        </Card>
      ) : null}

      {hasDailyActiveUsers ? <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <CardTitle>일일 활성 사용자 (DAU)</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                Firebase와 같은 일별 고유 사용자 추이입니다. 같은 앱 사용자는 KST 기준 하루 한 번만 집계합니다.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-md border bg-muted/40 p-1">
                {[7, 30, 90].map((days) => (
                  <Button
                    key={days}
                    type="button"
                    size="sm"
                    variant={selectedDateSpanDays === days ? 'secondary' : 'ghost'}
                    onClick={() => applyDailyActiveUsersRange(days)}
                  >
                    {days}일
                  </Button>
                ))}
              </div>
              <div className="inline-flex rounded-md border bg-muted/40 p-1">
                <Button
                  type="button"
                  size="sm"
                  variant={dailyActiveUsersView === 'chart' ? 'secondary' : 'ghost'}
                  className="gap-1.5"
                  onClick={() => setDailyActiveUsersView('chart')}
                >
                  <BarChart3 className="h-4 w-4" />
                  그래프
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={dailyActiveUsersView === 'table' ? 'secondary' : 'ghost'}
                  className="gap-1.5"
                  onClick={() => setDailyActiveUsersView('table')}
                >
                  <Table2 className="h-4 w-4" />
                  표
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {dailyActiveUsersError ? (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
              {dailyActiveUsersError}
            </div>
          ) : loading && !dailyActiveUsers ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              DAU 그래프를 불러오는 중입니다...
            </div>
          ) : dailyActiveUsers ? (
            <DailyActiveUsersPanel response={dailyActiveUsers} view={dailyActiveUsersView} />
          ) : (
            <p className="py-12 text-center text-sm text-muted-foreground">DAU 데이터가 없습니다.</p>
          )}
        </CardContent>
      </Card> : null}

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <CardTitle>일별 메트릭</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                {isSajugApplication
                  ? '일자별 핵심 퍼널, 결제, 평균 체류시간과 전일 대비 증감입니다.'
                  : `${application.label}의 앱 ID가 확인된 결제·구독 이벤트와 전일 대비 증감입니다.`}
              </p>
            </div>
            <div className="inline-flex w-fit rounded-md border bg-muted/40 p-1">
              <Button
                type="button"
                size="sm"
                variant={dailyMetricsView === 'chart' ? 'secondary' : 'ghost'}
                className="gap-1.5"
                onClick={() => setDailyMetricsView('chart')}
              >
                <BarChart3 className="h-4 w-4" />
                차트
              </Button>
              <Button
                type="button"
                size="sm"
                variant={dailyMetricsView === 'excel' ? 'secondary' : 'ghost'}
                className="gap-1.5"
                onClick={() => setDailyMetricsView('excel')}
              >
                <Table2 className="h-4 w-4" />
                엑셀
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && dailyMetricRows.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              일별 메트릭을 불러오는 중입니다...
            </div>
          ) : dailyMetricRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">일별 메트릭 데이터가 없습니다.</p>
          ) : (
            <div className="space-y-5">
              {isSajugApplication ? (
                <>
                  <DailyMetricSummaryGrid
                    latestRow={latestDailyMetricRow}
                    periodDwellAverageSeconds={periodDwellAverageSeconds}
                    periodDwellEvents={periodDwellEvents}
                  />
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm font-semibold">플랫폼별 일별 퍼널</p>
                    <div className="inline-flex w-fit rounded-md border bg-muted/40 p-1">
                      {[
                        { value: 'all' as const, label: '웹/모바일' },
                        { value: 'web' as const, label: '웹' },
                        { value: 'mobile' as const, label: '모바일' },
                        { value: 'unknown' as const, label: '미분류' },
                      ].map((option) => (
                        <Button
                          key={option.value}
                          type="button"
                          size="sm"
                          variant={platformDailyMetricsView === option.value ? 'secondary' : 'ghost'}
                          onClick={() => setPlatformDailyMetricsView(option.value)}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                  {platformAggregatedError ? (
                    <p className="rounded-lg border border-dashed border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
                      {platformAggregatedError}
                    </p>
                  ) : platformDailyMetricRows.length > 0 ? (
                    <PlatformDailyMetricsTable
                      rows={platformDailyMetricRows}
                      view={platformDailyMetricsView}
                    />
                  ) : (
                    <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                      플랫폼별 일별 메트릭 데이터가 없습니다.
                    </p>
                  )}
                </>
              ) : null}
              {dailyMetricsView === 'chart' ? (
                <DailyMetricsChart rows={dailyMetricRows} />
              ) : (
                <DailyMetricsExcelTable rows={dailyMetricRows} />
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {isSajugApplication ? <>
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <CardTitle>사주 백엔드 부하</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                사주 앱/API 활성 사용자와 현재 처리 중인 요청 기준입니다.
              </p>
            </div>
            <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
              {sajugMonitoring ? (
                <Badge variant={sajugMonitoring.capacity.status === 'normal' ? 'secondary' : 'warning'} className="w-fit">
                  {sajugMonitoring.service}
                </Badge>
              ) : null}
              <div className="inline-flex w-fit rounded-md border bg-muted/40 p-1">
                {[
                  { value: 'daily' as const, label: '일별' },
                  { value: 'summary' as const, label: '요약' },
                  { value: 'hourly' as const, label: '시간대' },
                ].map((option) => (
                  <Button
                    key={option.value}
                    type="button"
                    size="sm"
                    variant={sajugLoadView === option.value ? 'secondary' : 'ghost'}
                    onClick={() => setSajugLoadView(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {sajugMonitoringError ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
              {sajugMonitoringError}
            </div>
          ) : null}
          {sajugLoading && !sajugMonitoring && !sajugTraffic && !sajugDailyTraffic && !sajugErrors ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              사주 백엔드 부하 데이터를 불러오는 중입니다...
            </div>
          ) : sajugMonitoring || sajugTraffic || sajugDailyTraffic || sajugErrors ? (
            <>
              {sajugLoadView === 'daily' ? (
                <SajugTrafficDailyReport
                  rows={sajugTrafficDailyRows}
                  windowHours={sajugDailyTraffic?.windowHours ?? sajugTraffic?.windowHours ?? 0}
                />
              ) : null}
              {sajugLoadView === 'summary' ? (
                <SajugTrafficSummaryView
                  monitoringCards={sajugMonitoringCards}
                  trafficCards={sajugTrafficCards}
                  trafficRows={sajugTrafficRows}
                  bucketMinutes={sajugTraffic?.bucketMinutes ?? 60}
                  routes={topSajugRoutes}
                />
              ) : null}
              {sajugLoadView === 'hourly' ? (
                <div className="space-y-4">
                  {sajugTraffic ? (
                    <>
                      <SajugTrafficChart rows={sajugTrafficRows} bucketMinutes={sajugTraffic.bucketMinutes} />
                      <SajugTrafficHourlyTable rows={sajugTrafficRows} />
                    </>
                  ) : (
                    <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                      시간대별 동접 히스토리가 아직 없습니다.
                    </div>
                  )}
                </div>
              ) : null}
              <SajugErrorDetails response={sajugErrors} />
            </>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">사주 백엔드 부하 데이터가 없습니다.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>광고 퍼널</CardTitle>
          <p className="text-sm text-muted-foreground">
            웹은 결과 조회 후 Coupang, 모바일은 AdMob 배너/리워드 흐름을 단계별로 봅니다. 전환율은 이벤트 수 기준입니다.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          {adFunnelError ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
              {adFunnelError}
            </div>
          ) : null}
          {!adFunnelError && adFunnelRows.every((row) => row.count === 0) ? (
            <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
              광고 퍼널 데이터가 아직 없습니다.
            </p>
          ) : !adFunnelError ? (
            <>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                {adFunnelRows.map((row) => (
                  <div key={row.code} className="rounded-lg border p-3">
                    <p className="text-sm font-medium">{formatAdFunnelStepLabel(row.code)}</p>
                    <p className="mt-2 text-2xl font-semibold">{formatMetricNumber(row.count)}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      이전 단계 대비 {formatMetricPercent(row.conversionFromPrevious)}
                    </p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <AdFunnelPlatformPanel title="웹 (Coupang)" rows={webAdFunnelRows} />
                <AdFunnelPlatformPanel title="모바일 (AdMob)" rows={mobileAdFunnelRows} />
              </div>
              <AdFunnelBreakdownTable
                providers={adFunnelSummary?.providers ?? []}
                placements={adFunnelSummary?.placements ?? []}
              />
            </>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>가입 퍼널</CardTitle>
            {funnelImportantIssues.length > 0 ? (
              <Badge variant="warning" className="w-fit gap-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                +{funnelImportantIssues.length} 주의
              </Badge>
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground">
            큰 숫자는 앞 단계를 순서대로 통과한 사용자/익명 ID 기준입니다. 실제 고유 사용자와 반복 이벤트는 함께 표시합니다.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {funnelImportantIssues.length > 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
              <p className="font-semibold">확인 필요한 퍼널 이벤트</p>
              <div className="mt-2 space-y-2">
                {funnelImportantIssues.map((issue) => (
                  <FunnelIssueRow key={issue.code} issue={issue} />
                ))}
              </div>
            </div>
          ) : null}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
            {funnelRows.map((row) => (
              <div key={row.code} className={`rounded-lg border p-3 ${row.isOutOfOrder ? 'border-amber-300 bg-amber-50/60' : ''}`}>
                <p className="text-sm font-medium">{row.label}</p>
                <p className="mt-2 text-xs text-muted-foreground">순차 도달</p>
                <p className="mt-2 text-2xl font-semibold">{formatMetricNumber(row.count)}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  고유 {formatMetricNumber(row.uniqueActorCount)}명 / 이벤트 {formatMetricNumber(row.eventCount)}회
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  이전 단계 대비 {formatMetricPercent(row.conversionFromPrevious)}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  앱 열기 대비 {formatMetricPercent(row.conversionFromFirst)}
                </p>
              </div>
            ))}
          </div>
          <AnonymousDeviceFunnelPanel rows={anonymousDeviceFunnelRows} />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {platformFunnelRows.map(({ platform, rows }) => (
              <div key={platform} className="rounded-lg border p-4">
                <p className="text-sm font-semibold">{platform === 'mobile' ? '모바일' : '웹'}</p>
                <div className="mt-3 space-y-2">
                  {rows.map((row) => (
                    <div key={`${platform}-${row.code}`} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">{row.label}</span>
                      <span className="text-right font-medium">
                        순차 {formatMetricNumber(row.count)} / {formatMetricPercent(row.conversionFromFirst)}
                        <span className="block text-xs font-normal text-muted-foreground">
                          고유 {formatMetricNumber(row.uniqueActorCount)}명 / 이벤트 {formatMetricNumber(row.eventCount)}회
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>기능별 요청/체류</CardTitle>
          <p className="text-sm text-muted-foreground">
            사주 API 라우트, 기능 이벤트, 페이지 체류 시간을 요청량 기준으로 합쳐 봅니다.
          </p>
        </CardHeader>
        <CardContent>
          <FeatureEngagementTable rows={featureEngagementRows} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>페이지 체류 시간</CardTitle>
          <p className="text-sm text-muted-foreground">
            쿼리스트링을 제외한 라우트 기준입니다. 막대는 총 체류시간, 오른쪽 값은 평균 체류시간입니다.
          </p>
        </CardHeader>
        <CardContent>
          {dwellWeeklySummary ? <DwellWeeklySummaryPanel summary={dwellWeeklySummary} /> : null}
          {pageDwellLoading && pageDwellRows.length === 0 && periodDwellEvents === 0 ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              체류시간 데이터를 불러오는 중입니다...
            </div>
          ) : pageDwellRows.length === 0 ? (
            periodDwellEvents > 0 ? (
              <div className="rounded-lg border border-dashed p-4">
                <p className="text-sm font-semibold">기간 평균 체류시간 {formatDurationSeconds(periodDwellAverageSeconds)}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  총 {formatDurationSeconds(periodDwellTotalSeconds)} · 이벤트 {formatMetricNumber(periodDwellEvents)}회
                </p>
              </div>
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">체류시간 데이터가 없습니다.</p>
            )
          ) : (
            <div className="space-y-4">
              {pageDwellRows.map((row) => {
                const width = `${Math.max(4, Math.round((row.totalSeconds / pageDwellMaxTotal) * 100))}%`;
                const platformRows = (pageDwellPlatformRowsByPath.get(row.path) ?? [])
                  .filter((platformRow) => platformRow.platform === 'web' || platformRow.platform === 'mobile')
                  .sort((left, right) => left.platform.localeCompare(right.platform));

                return (
                  <div key={row.path} className="space-y-2 rounded-lg border p-4">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <p className="break-all text-sm font-semibold">{row.path}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          총 {formatDurationSeconds(row.totalSeconds)} · 고유 {formatMetricNumber(row.uniqueActorCount)}명 · 이벤트 {formatMetricNumber(row.eventCount)}회
                        </p>
                      </div>
                      <div className="text-left sm:text-right">
                        <p className="text-lg font-semibold">{formatDurationSeconds(row.averageSeconds)}</p>
                        <p className="text-xs text-muted-foreground">평균</p>
                      </div>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-primary" style={{ width }} />
                    </div>
                    {platformRows.length > 0 ? (
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        {platformRows.map((platformRow) => (
                          <span key={`${row.path}-${platformRow.platform}`} className="rounded-full border px-2 py-1">
                            {platformRow.platform === 'mobile' ? '모바일' : '웹'} 평균 {formatDurationSeconds(platformRow.averageSeconds)}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      </> : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle>상세 메트릭</CardTitle>
              <div className="inline-flex w-fit rounded-md border bg-muted/40 p-1">
                {[
                  { value: 'daily' as const, label: '일별' },
                  { value: 'raw' as const, label: '원본' },
                ].map((option) => (
                  <Button
                    key={option.value}
                    type="button"
                    size="sm"
                    variant={detailMetricsView === option.value ? 'secondary' : 'ghost'}
                    onClick={() => setDetailMetricsView(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {detailMetricsView === 'daily' ? (
              loading && dailyMetricRows.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  일별 상세 메트릭을 불러오는 중입니다...
                </div>
              ) : dailyMetricRows.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">일별 상세 메트릭 데이터가 없습니다.</p>
              ) : (
                <DailyMetricsExcelTable rows={dailyMetricRows} />
              )
            ) : (
              <Table mobileCards>
                <TableHeader>
                  <TableRow>
                    <TableHead>시각</TableHead>
                    <TableHead>타입</TableHead>
                    <TableHead>이름</TableHead>
                    <TableHead>플랫폼</TableHead>
                    <TableHead className="text-right">값</TableHead>
                    <TableHead>태그</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailMetricsLoading ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <div className="flex items-center justify-center py-8 text-muted-foreground">
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          메트릭 데이터를 불러오는 중입니다...
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : metrics.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                        표시할 메트릭이 없습니다.
                      </TableCell>
                    </TableRow>
                  ) : (
                    metrics.map((metric, index) => (
                      <TableRow key={metric.id ?? `${metric.type ?? 'metric'}-${index}`}>
                        <TableCell>{formatMetricTimestamp(metric.timestamp)}</TableCell>
                        <TableCell>{metric.type ?? '-'}</TableCell>
                        <TableCell>{metric.name ?? '-'}</TableCell>
                        <TableCell>{formatMetricPaymentPlatform(metric.tags)}</TableCell>
                        <TableCell className="text-right">{formatMetricNumber(metric.value)}</TableCell>
                        <TableCell className="max-w-xs break-words text-xs text-muted-foreground">
                          {formatMetricTags(metric.tags)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>요약</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {summaryEntries.length === 0 ? (
                <p className="text-sm text-muted-foreground">요약 데이터가 없습니다.</p>
              ) : (
                summaryEntries.slice(0, 6).map(([metricName, item]) => (
                  <div key={metricName} className="rounded-lg border p-3">
                    <p className="text-sm font-medium">{metricName}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      count {formatMetricNumber(item.count)} / total {formatMetricNumber(item.total)}
                    </p>
                    <p className="mt-1 text-lg font-semibold">{formatMetricNumber(item.average)}</p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
}

function AnonymousDeviceFunnelPanel({ rows }: { rows: FunnelRow[] }) {
  const hasData = rows.some((row) => row.eventCount > 0 || row.count > 0);
  const visibleRows = rows.slice(0, 4);

  return (
    <div className="rounded-lg border bg-muted/20 p-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-semibold">비로그인 기기 이탈</p>
          <p className="mt-1 text-xs text-muted-foreground">
            로그인 전 사용자는 계정 대신 익명 기기 ID 기준으로 순차 도달을 계산합니다.
          </p>
        </div>
        {hasData ? (
          <Badge variant="secondary" className="w-fit">
            기기 기준
          </Badge>
        ) : null}
      </div>

      {!hasData ? (
        <p className="mt-4 rounded-md border border-dashed bg-background px-3 py-3 text-sm text-muted-foreground">
          비로그인 기기 퍼널 데이터가 아직 없습니다.
        </p>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {visibleRows.map((row) => (
            <div key={`anonymous-${row.code}`} className="rounded-md border bg-background p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium">{row.label}</p>
                <span className="text-xs text-muted-foreground">{formatMetricPercent(row.conversionFromPrevious)}</span>
              </div>
              <p className="mt-2 text-2xl font-semibold">{formatMetricNumber(row.count)}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                고유 {formatMetricNumber(row.uniqueActorCount)} / 이벤트 {formatMetricNumber(row.eventCount)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function sourceLabel(source: FeatureEngagementRow['sources'][number]) {
  if (source === 'route') {
    return 'API';
  }
  if (source === 'dwell') {
    return '체류';
  }
  return '기능';
}

function FeatureEngagementTable({ rows }: { rows: FeatureEngagementRow[] }) {
  if (rows.length === 0) {
    return (
      <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
        기능별 요청/체류 데이터가 아직 없습니다.
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border">
      <Table mobileCards className="min-w-[920px]">
        <TableHeader className="bg-muted/60">
          <TableRow>
            <TableHead>기능/위치</TableHead>
            <TableHead>구분</TableHead>
            <TableHead className="text-right">요청/이벤트</TableHead>
            <TableHead className="text-right">고유 기기</TableHead>
            <TableHead className="text-right">총 체류</TableHead>
            <TableHead className="text-right">평균 체류</TableHead>
            <TableHead className="text-right">오류율</TableHead>
            <TableHead className="text-right">p95</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              <TableCell className="max-w-sm break-all font-medium">{row.label}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {row.sources.map((source) => (
                    <Badge key={`${row.key}-${source}`} variant="outline" className="w-fit">
                      {sourceLabel(source)}
                    </Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell className="text-right">{formatMetricNumber(row.requestCount)}</TableCell>
              <TableCell className="text-right">{row.uniqueActorCount > 0 ? formatMetricNumber(row.uniqueActorCount) : '-'}</TableCell>
              <TableCell className="text-right">
                {row.dwellTotalSeconds > 0 ? formatDurationSeconds(row.dwellTotalSeconds) : '-'}
              </TableCell>
              <TableCell className="text-right">
                {row.dwellAverageSeconds > 0 ? formatDurationSeconds(row.dwellAverageSeconds) : '-'}
              </TableCell>
              <TableCell className="text-right">{row.errorRate !== null ? formatMetricPercent(row.errorRate) : '-'}</TableCell>
              <TableCell className="text-right">{row.p95LatencyMs !== null ? `${formatMetricNumber(row.p95LatencyMs)}ms` : '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function MetricsViewModeControl<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: MetricsViewModeOption<T>[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="min-w-0 space-y-2">
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <div className="flex w-full rounded-md border bg-muted/40 p-1">
        {options.map((option) => {
          const Icon = option.icon;
          return (
            <Button
              key={option.value}
              type="button"
              size="sm"
              variant={value === option.value ? 'secondary' : 'ghost'}
              className="h-10 min-w-0 flex-1 justify-center gap-1.5 px-2 text-xs sm:text-sm"
              onClick={() => onChange(option.value)}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{option.label}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
}

function trendToneClass(cell: DailyMetricCell, goodDirection: 'up' | 'down' | 'neutral' = 'up') {
  if (cell.trend === 'flat') {
    return 'border-slate-200 bg-slate-50 text-slate-600';
  }
  if (goodDirection === 'neutral') {
    return cell.trend === 'up'
      ? 'border-sky-200 bg-sky-50 text-sky-700'
      : 'border-amber-200 bg-amber-50 text-amber-700';
  }
  const isGood = cell.trend === goodDirection;
  return isGood
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : 'border-rose-200 bg-rose-50 text-rose-700';
}

function TrendBadge({
  cell,
  kind,
  goodDirection = 'up',
  className = '',
}: {
  cell: DailyMetricCell;
  kind: 'count' | 'currency' | 'duration';
  goodDirection?: 'up' | 'down' | 'neutral';
  className?: string;
}) {
  const Icon = cell.trend === 'up' ? TrendingUp : cell.trend === 'down' ? TrendingDown : Minus;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${trendToneClass(cell, goodDirection)} ${className}`}
    >
      <Icon className="h-3 w-3" />
      {formatDailyMetricChange(cell, kind)}
    </span>
  );
}

function DailyMetricSummaryGrid({
  latestRow,
  periodDwellAverageSeconds,
  periodDwellEvents,
}: {
  latestRow: DailyMetricRow | null;
  periodDwellAverageSeconds: number;
  periodDwellEvents: number;
}) {
  if (!latestRow) {
    return null;
  }

  const cards = [
    {
      label: '총 이벤트',
      value: formatDailyMetricValue(latestRow.totalEvents, 'count'),
      cell: latestRow.totalEvents,
      kind: 'count' as const,
      goodDirection: 'neutral' as const,
    },
    {
      label: '결과 조회',
      value: formatDailyMetricValue(latestRow.resultViewed, 'count'),
      cell: latestRow.resultViewed,
      kind: 'count' as const,
      goodDirection: 'up' as const,
    },
    {
      label: '구매 완료',
      value: formatDailyMetricValue(latestRow.purchaseCompleted, 'count'),
      cell: latestRow.purchaseCompleted,
      kind: 'count' as const,
      goodDirection: 'up' as const,
    },
    {
      label: '평균 체류',
      value: formatDurationSeconds(periodDwellAverageSeconds),
      cell: latestRow.dwellAverageSeconds,
      kind: 'duration' as const,
      goodDirection: 'neutral' as const,
      detail: `체류 이벤트 ${formatMetricNumber(periodDwellEvents)}회`,
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => (
        <div key={card.label} className="rounded-lg border bg-background p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{card.label}</p>
              <p className="mt-2 text-2xl font-semibold">{card.value}</p>
            </div>
            <TrendBadge cell={card.cell} kind={card.kind} goodDirection={card.goodDirection} />
          </div>
          {card.detail ? <p className="mt-2 text-xs text-muted-foreground">{card.detail}</p> : null}
        </div>
      ))}
    </div>
  );
}

function DwellWeeklySummaryPanel({ summary }: { summary: DwellWeeklySummary }) {
  const currentRange = summary.currentStartDate === summary.currentEndDate
    ? summary.currentEndDate
    : `${summary.currentStartDate} ~ ${summary.currentEndDate}`;
  const previousRange = summary.previousStartDate && summary.previousEndDate
    ? `${summary.previousStartDate} ~ ${summary.previousEndDate}`
    : '비교 기간 없음';
  const cards = [
    {
      label: '최근 7일 총 체류',
      value: formatDurationSeconds(summary.totalSeconds.value),
      cell: summary.totalSeconds,
      kind: 'duration' as const,
    },
    {
      label: '최근 7일 평균 체류',
      value: formatDurationSeconds(summary.averageSeconds.value),
      cell: summary.averageSeconds,
      kind: 'duration' as const,
    },
    {
      label: '최근 7일 체류 이벤트',
      value: `${formatMetricNumber(summary.events.value)}회`,
      cell: summary.events,
      kind: 'count' as const,
    },
  ];

  return (
    <div className="mb-4 rounded-lg border bg-muted/30 p-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-semibold">체류시간 7일 증감</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {currentRange} · 이전 {summary.previousDayCount}일({previousRange}) 대비
          </p>
        </div>
        <Badge variant="outline" className="w-fit">
          최근 {summary.dayCount}일
        </Badge>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
        {cards.map((card) => (
          <div key={card.label} className="rounded-md border bg-background p-3">
            <p className="text-xs font-medium text-muted-foreground">{card.label}</p>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xl font-semibold">{card.value}</p>
              <TrendBadge cell={card.cell} kind={card.kind} goodDirection="neutral" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DailyMetricsChart({ rows }: { rows: DailyMetricRow[] }) {
  const chartRows = rows.slice(-14);
  const maxEvents = Math.max(1, ...chartRows.map((row) => row.totalEvents.value));
  const maxDwell = Math.max(1, ...chartRows.map((row) => row.dwellAverageSeconds.value));
  const gridTemplateColumns = `repeat(${Math.max(chartRows.length, 1)}, minmax(54px, 1fr))`;

  return (
    <div className="rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold">최근 일별 흐름</p>
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm bg-primary" />
            총 이벤트
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm bg-emerald-500" />
            결과 조회
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm bg-amber-500" />
            평균 체류
          </span>
        </div>
      </div>
      <div className="mt-4 overflow-x-auto pb-2">
        <div className="min-w-[760px]">
          <div className="grid h-56 items-end gap-3 border-b" style={{ gridTemplateColumns }}>
            {chartRows.map((row) => {
              const eventHeight = Math.max(4, Math.round((row.totalEvents.value / maxEvents) * 100));
              const resultHeight = row.resultViewed.value > 0
                ? Math.max(4, Math.round((row.resultViewed.value / maxEvents) * 100))
                : 0;
              const dwellHeight = row.dwellAverageSeconds.value > 0
                ? Math.max(4, Math.round((row.dwellAverageSeconds.value / maxDwell) * 100))
                : 0;

              return (
                <div
                  key={row.date}
                  className="flex h-full min-w-0 flex-col justify-end gap-2"
                  title={`${row.date} · 총 ${formatMetricNumber(row.totalEvents.value)}회 · 결과 ${formatMetricNumber(row.resultViewed.value)}회 · 평균 체류 ${formatDurationSeconds(row.dwellAverageSeconds.value)}`}
                >
                  <div className="relative flex h-44 items-end justify-center rounded-t bg-muted/50 px-2">
                    <div className="w-full max-w-8 rounded-t bg-primary/75" style={{ height: `${eventHeight}%` }} />
                    <div
                      className="absolute bottom-0 left-1/2 w-3 -translate-x-1/2 rounded-t bg-emerald-500"
                      style={{ height: `${resultHeight}%` }}
                    />
                    <div
                      className="absolute bottom-0 right-2 w-1.5 rounded-t bg-amber-500"
                      style={{ height: `${dwellHeight}%` }}
                    />
                  </div>
                  <p className="truncate text-center text-[11px] text-muted-foreground">{row.date.slice(5)}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-3 grid gap-3" style={{ gridTemplateColumns }}>
            {chartRows.map((row) => (
              <div key={`${row.date}-trend`} className="min-w-0 text-center">
                <TrendBadge cell={row.totalEvents} kind="count" goodDirection="neutral" className="justify-center" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function DailyMetricTableValue({
  cell,
  kind,
  goodDirection = 'up',
}: {
  cell: DailyMetricCell;
  kind: 'count' | 'currency' | 'duration';
  goodDirection?: 'up' | 'down' | 'neutral';
}) {
  return (
    <div className="min-w-[92px] text-right">
      <p className="font-medium">{formatDailyMetricValue(cell, kind)}</p>
      <TrendBadge cell={cell} kind={kind} goodDirection={goodDirection} className="mt-1" />
    </div>
  );
}

function DailyMetricsExcelTable({ rows }: { rows: DailyMetricRow[] }) {
  const tableRows = [...rows].reverse();

  return (
    <div className="overflow-hidden rounded-lg border">
      <Table mobileCards className="min-w-[1180px] border-collapse">
        <TableHeader className="bg-muted/60">
          <TableRow className="[&>th]:border-r [&>th:last-child]:border-r-0">
            <TableHead className="sticky left-0 z-10 bg-muted/95">일자</TableHead>
            <TableHead className="text-right">총 이벤트</TableHead>
            <TableHead className="text-right">앱 열기</TableHead>
            <TableHead className="text-right">입력 시작</TableHead>
            <TableHead className="text-right">결과 조회</TableHead>
            <TableHead className="text-right">로그인</TableHead>
            <TableHead className="text-right">구매</TableHead>
            <TableHead className="text-right">결제 실패</TableHead>
            <TableHead className="text-right">매출</TableHead>
            <TableHead className="text-right">평균 체류</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tableRows.map((row) => (
            <TableRow key={row.date} className="[&>td]:border-r [&>td:last-child]:border-r-0">
              <TableCell className="sticky left-0 z-10 bg-background font-medium">{row.date}</TableCell>
              <TableCell><DailyMetricTableValue cell={row.totalEvents} kind="count" goodDirection="neutral" /></TableCell>
              <TableCell><DailyMetricTableValue cell={row.appOpened} kind="count" /></TableCell>
              <TableCell><DailyMetricTableValue cell={row.formStarted} kind="count" /></TableCell>
              <TableCell><DailyMetricTableValue cell={row.resultViewed} kind="count" /></TableCell>
              <TableCell><DailyMetricTableValue cell={row.loginCompleted} kind="count" /></TableCell>
              <TableCell><DailyMetricTableValue cell={row.purchaseCompleted} kind="count" /></TableCell>
              <TableCell><DailyMetricTableValue cell={row.paymentFailed} kind="count" goodDirection="down" /></TableCell>
              <TableCell><DailyMetricTableValue cell={row.revenue} kind="currency" /></TableCell>
              <TableCell>
                <div className="text-right">
                  <DailyMetricTableValue cell={row.dwellAverageSeconds} kind="duration" goodDirection="neutral" />
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    체류 {formatMetricNumber(row.dwellEvents.value)}회
                  </p>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function TrafficTrendBadge({
  cell,
  kind,
  goodDirection = 'up',
  className = '',
}: {
  cell: DailyMetricCell;
  kind: TrafficMetricKind;
  goodDirection?: 'up' | 'down' | 'neutral';
  className?: string;
}) {
  const Icon = cell.trend === 'up' ? TrendingUp : cell.trend === 'down' ? TrendingDown : Minus;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${trendToneClass(cell, goodDirection)} ${className}`}
    >
      <Icon className="h-3 w-3" />
      {formatTrafficMetricChange(cell, kind)}
    </span>
  );
}

function TrafficMetricValue({
  cell,
  kind,
  goodDirection = 'up',
}: {
  cell: DailyMetricCell;
  kind: TrafficMetricKind;
  goodDirection?: 'up' | 'down' | 'neutral';
}) {
  return (
    <div className="min-w-[104px] text-right">
      <p className="font-medium">{formatTrafficMetricValue(cell.value, kind)}</p>
      <TrafficTrendBadge cell={cell} kind={kind} goodDirection={goodDirection} className="mt-1" />
    </div>
  );
}

const shortDauDate = (value: string) => {
  const [, month = '', day = ''] = value.split('-');
  return `${Number(month)}.${Number(day)}`;
};

const dauScaleMax = (rows: DailyActiveUserPoint[]) => {
  const rawMax = Math.max(1, ...rows.flatMap((row) => [row.total, row.web, row.mobile]));
  const roughStep = rawMax / 4;
  const magnitude = 10 ** Math.floor(Math.log10(Math.max(1, roughStep)));
  const normalized = roughStep / magnitude;
  const stepFactor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return Math.max(4, stepFactor * magnitude * 4);
};

function DailyActiveUsersPanel({
  response,
  view,
}: {
  response: DailyActiveUsersResponse;
  view: DailyActiveUsersView;
}) {
  const rows = response.series ?? [];
  const [activeIndex, setActiveIndex] = useState(Math.max(0, rows.length - 1));

  useEffect(() => {
    setActiveIndex(Math.max(0, rows.length - 1));
  }, [response.startDate, response.endDate, rows.length]);

  const safeActiveIndex = Math.min(Math.max(0, activeIndex), Math.max(0, rows.length - 1));
  const activeRow = rows[safeActiveIndex] ?? null;
  const summary = response.summary;
  const changeTone = summary.change > 0
    ? 'text-emerald-700'
    : summary.change < 0
      ? 'text-rose-700'
      : 'text-muted-foreground';
  const changeLabel = summary.changePercent === null
    ? `${formatSignedMetricNumber(summary.change, '명')} · 전일 비교 불가`
    : `${formatSignedMetricNumber(summary.change, '명')} · ${formatSignedMetricNumber(summary.changePercent, '%')}`;

  if (rows.length === 0) {
    return <p className="py-12 text-center text-sm text-muted-foreground">선택 기간의 DAU 데이터가 없습니다.</p>;
  }

  if (view === 'table') {
    return (
      <div className="overflow-hidden rounded-lg border">
        <Table mobileCards>
          <TableHeader className="bg-muted/60">
            <TableRow>
              <TableHead>일자 (KST)</TableHead>
              <TableHead className="text-right">전체 DAU</TableHead>
              <TableHead className="text-right">웹</TableHead>
              <TableHead className="text-right">모바일</TableHead>
              <TableHead className="text-right">미분류</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...rows].reverse().map((row) => (
              <TableRow key={row.date}>
                <TableCell className="font-medium">{row.date}</TableCell>
                <TableCell className="text-right font-semibold">{formatMetricNumber(row.total)}명</TableCell>
                <TableCell className="text-right">{formatMetricNumber(row.web)}명</TableCell>
                <TableCell className="text-right">{formatMetricNumber(row.mobile)}명</TableCell>
                <TableCell className="text-right text-muted-foreground">{formatMetricNumber(row.unknown)}명</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  const width = 960;
  const height = 320;
  const left = 48;
  const right = 20;
  const top = 24;
  const bottom = 42;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const maxY = dauScaleMax(rows);
  const xFor = (index: number) => left + (index / Math.max(1, rows.length - 1)) * plotWidth;
  const yFor = (value: number) => top + plotHeight - (value / maxY) * plotHeight;
  const linePath = (key: 'total' | 'web' | 'mobile') => rows
    .map((row, index) => `${index === 0 ? 'M' : 'L'} ${xFor(index)} ${yFor(row[key])}`)
    .join(' ');
  const totalPath = linePath('total');
  const areaPath = `${totalPath} L ${xFor(rows.length - 1)} ${top + plotHeight} L ${xFor(0)} ${top + plotHeight} Z`;
  const xLabelInterval = Math.max(1, Math.ceil(rows.length / 6));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-xl border bg-background p-4">
          <p className="text-sm text-muted-foreground">{response.endDate === toDateInputValue(new Date()) ? '오늘 DAU' : '종료일 DAU'}</p>
          <p className="mt-2 text-3xl font-semibold tracking-tight">{formatMetricNumber(summary.current)}명</p>
          <p className={`mt-1 text-xs font-medium ${changeTone}`}>{changeLabel}</p>
        </div>
        <div className="rounded-xl border bg-background p-4">
          <p className="text-sm text-muted-foreground">기간 평균</p>
          <p className="mt-2 text-3xl font-semibold tracking-tight">{formatMetricNumber(summary.average)}명</p>
          <p className="mt-1 text-xs text-muted-foreground">일평균 고유 사용자</p>
        </div>
        <div className="rounded-xl border bg-background p-4">
          <p className="text-sm text-muted-foreground">기간 최고</p>
          <p className="mt-2 text-3xl font-semibold tracking-tight">{formatMetricNumber(summary.peak)}명</p>
          <p className="mt-1 text-xs text-muted-foreground">{summary.peakDate ?? '-'}</p>
        </div>
        <div className="rounded-xl border bg-background p-4">
          <p className="text-sm text-muted-foreground">집계 기준</p>
          <p className="mt-2 text-lg font-semibold">고유 앱 사용자</p>
          <p className="mt-1 text-xs text-muted-foreground">Asia/Seoul · 일별 중복 제거</p>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border bg-background">
        <div className="flex flex-col gap-3 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold">활성 사용자 추이</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {activeRow
                ? `${activeRow.date} · 전체 ${formatMetricNumber(activeRow.total)}명 · 웹 ${formatMetricNumber(activeRow.web)}명 · 모바일 ${formatMetricNumber(activeRow.mobile)}명`
                : '날짜 위에 포인터를 올리면 상세 값을 확인할 수 있습니다.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-primary" />전체</span>
            <span className="flex items-center gap-1.5"><span className="h-0.5 w-4 bg-slate-500" />웹</span>
            <span className="flex items-center gap-1.5"><span className="h-0.5 w-4 bg-emerald-500" />모바일</span>
          </div>
        </div>
        <div className="overflow-x-auto px-2 pb-2 pt-3 sm:px-4">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            className="min-w-[720px] w-full"
            role="img"
            aria-label={`${response.startDate}부터 ${response.endDate}까지의 일일 활성 사용자 선 그래프`}
          >
            <defs>
              <linearGradient id="dau-area-gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.2" />
                <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.01" />
              </linearGradient>
            </defs>
            {[0, 1, 2, 3, 4].map((tick) => {
              const value = (maxY / 4) * tick;
              const y = yFor(value);
              return (
                <g key={tick}>
                  <line x1={left} x2={width - right} y1={y} y2={y} className="stroke-border" strokeWidth="1" />
                  <text x={left - 9} y={y + 4} textAnchor="end" className="fill-muted-foreground text-[11px]">
                    {formatMetricNumber(value)}
                  </text>
                </g>
              );
            })}
            <path d={areaPath} fill="url(#dau-area-gradient)" />
            <path d={linePath('web')} fill="none" stroke="#64748b" strokeWidth="2" strokeDasharray="5 5" />
            <path d={linePath('mobile')} fill="none" stroke="#10b981" strokeWidth="2" strokeDasharray="5 5" />
            <path d={totalPath} fill="none" stroke="var(--primary)" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
            {rows.map((row, index) => {
              const x = xFor(index);
              const y = yFor(row.total);
              const selected = index === safeActiveIndex;
              return (
                <g
                  key={row.date}
                  tabIndex={0}
                  role="button"
                  aria-label={`${row.date}, 전체 ${row.total}명, 웹 ${row.web}명, 모바일 ${row.mobile}명`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onFocus={() => setActiveIndex(index)}
                  onClick={() => setActiveIndex(index)}
                  className="cursor-pointer outline-none"
                >
                  {selected ? (
                    <line x1={x} x2={x} y1={top} y2={top + plotHeight} stroke="var(--primary)" strokeOpacity="0.2" />
                  ) : null}
                  <circle cx={x} cy={y} r="11" fill="transparent" />
                  <circle
                    cx={x}
                    cy={y}
                    r={selected ? 5 : 3}
                    fill="var(--background)"
                    stroke="var(--primary)"
                    strokeWidth={selected ? 3 : 2}
                  >
                    <title>{`${row.date} · DAU ${row.total}명`}</title>
                  </circle>
                </g>
              );
            })}
            {rows.map((row, index) => (
              index % xLabelInterval === 0 || index === rows.length - 1 ? (
                <text
                  key={`${row.date}-label`}
                  x={xFor(index)}
                  y={height - 14}
                  textAnchor={index === 0 ? 'start' : index === rows.length - 1 ? 'end' : 'middle'}
                  className="fill-muted-foreground text-[11px]"
                >
                  {shortDauDate(row.date)}
                </text>
              ) : null
            ))}
          </svg>
        </div>
      </div>
    </div>
  );
}

const SAJUG_DAILY_PAGE_SIZE = 10;

function SajugTrafficDailyReport({ rows, windowHours }: { rows: SajugTrafficDailyRow[]; windowHours: number }) {
  const [page, setPage] = useState(0);
  const tableRows = [...rows].reverse();
  const totalRequests = rows.reduce((total, row) => total + row.requests.value, 0);
  const totalErrors = rows.reduce((total, row) => total + row.errors.value, 0);
  const peakActiveUsers = Math.max(0, ...rows.map((row) => row.peakActiveUsers.value));
  const weightedLatency = rows.reduce((total, row) => total + row.avgLatencyMs.value * row.requests.value, 0);
  const avgLatencyMs = totalRequests > 0 ? weightedLatency / totalRequests : 0;
  const errorRate = totalRequests > 0 ? (totalErrors / totalRequests) * 100 : 0;
  const reportDays = Math.max(1, Math.ceil(windowHours / 24));

  const totalPages = Math.max(1, Math.ceil(tableRows.length / SAJUG_DAILY_PAGE_SIZE));
  // page는 자동 새로고침으로 행 수가 줄어도 범위를 벗어나지 않게 표시 시점에 clamp한다.
  const safePage = Math.min(page, totalPages - 1);
  const pageStart = safePage * SAJUG_DAILY_PAGE_SIZE;
  const pagedRows = tableRows.slice(pageStart, pageStart + SAJUG_DAILY_PAGE_SIZE);

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
        일별 사주 부하 데이터가 아직 없습니다.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {[
          { label: `최근 ${formatMetricNumber(reportDays)}일 요청`, value: formatTrafficMetricValue(totalRequests, 'requests') },
          { label: '오류율', value: formatTrafficMetricValue(errorRate, 'percent') },
          { label: '피크 동접', value: formatTrafficMetricValue(peakActiveUsers, 'users') },
          { label: '평균 응답', value: formatTrafficMetricValue(avgLatencyMs, 'latency') },
        ].map((item) => (
          <div key={item.label} className="rounded-lg border bg-background p-3">
            <p className="text-sm font-medium text-muted-foreground">{item.label}</p>
            <p className="mt-2 text-2xl font-semibold">{item.value}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg border">
        <Table mobileCards className="min-w-[1180px] border-collapse">
          <TableHeader className="bg-muted/60">
            <TableRow className="[&>th]:border-r [&>th:last-child]:border-r-0">
              <TableHead className="sticky left-0 z-10 bg-muted/95">일자</TableHead>
              <TableHead>피크 시간</TableHead>
              <TableHead className="text-right">피크 동접</TableHead>
              <TableHead className="text-right">로그인 피크</TableHead>
              <TableHead className="text-right">익명 피크</TableHead>
              <TableHead className="text-right">요청</TableHead>
              <TableHead className="text-right">오류</TableHead>
              <TableHead className="text-right">오류율</TableHead>
              <TableHead className="text-right">평균 응답</TableHead>
              <TableHead className="text-right">처리중 피크</TableHead>
              <TableHead className="text-right">버킷</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedRows.map((row) => (
              <TableRow key={row.date} className="[&>td]:border-r [&>td:last-child]:border-r-0">
                <TableCell className="sticky left-0 z-10 bg-background font-medium">{row.date}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{row.busiestLabel}</TableCell>
                <TableCell><TrafficMetricValue cell={row.peakActiveUsers} kind="users" goodDirection="neutral" /></TableCell>
                <TableCell><TrafficMetricValue cell={row.authenticatedPeak} kind="users" goodDirection="neutral" /></TableCell>
                <TableCell><TrafficMetricValue cell={row.anonymousPeak} kind="users" goodDirection="neutral" /></TableCell>
                <TableCell><TrafficMetricValue cell={row.requests} kind="requests" /></TableCell>
                <TableCell><TrafficMetricValue cell={row.errors} kind="errors" goodDirection="down" /></TableCell>
                <TableCell><TrafficMetricValue cell={row.errorRate} kind="percent" goodDirection="down" /></TableCell>
                <TableCell><TrafficMetricValue cell={row.avgLatencyMs} kind="latency" goodDirection="down" /></TableCell>
                <TableCell><TrafficMetricValue cell={row.activeRequestsPeak} kind="activeRequests" goodDirection="down" /></TableCell>
                <TableCell className="text-right text-sm text-muted-foreground">{formatMetricNumber(row.bucketCount)}개</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 ? (
        <div className="flex flex-wrap items-center justify-between gap-2 px-1">
          <p className="text-sm text-muted-foreground">
            {`${formatMetricNumber(pageStart + 1)}–${formatMetricNumber(pageStart + pagedRows.length)} / 총 ${formatMetricNumber(tableRows.length)}일`}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={safePage <= 0}
              onClick={() => setPage(safePage - 1)}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              이전
            </Button>
            <span className="text-sm tabular-nums text-muted-foreground">
              {`${formatMetricNumber(safePage + 1)} / ${formatMetricNumber(totalPages)}`}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={safePage >= totalPages - 1}
              onClick={() => setPage(safePage + 1)}
            >
              다음
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SajugTrafficSummaryView({
  monitoringCards,
  trafficCards,
  trafficRows,
  bucketMinutes,
  routes,
}: {
  monitoringCards: SajugMonitoringCard[];
  trafficCards: SajugMonitoringCard[];
  trafficRows: SajugTrafficChartRow[];
  bucketMinutes: number;
  routes: SajugMonitoringSnapshot['routes'];
}) {
  return (
    <div className="space-y-5">
      {monitoringCards.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
          {monitoringCards.map((card, index) => {
            const Icon = MONITORING_CARD_ICONS[index] ?? Activity;
            return (
              <div key={card.label} className={`rounded-lg border p-3 ${monitoringToneClass[card.tone]}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium">{card.label}</p>
                  <Icon className="h-4 w-4 shrink-0 opacity-70" />
                </div>
                <p className="mt-2 text-2xl font-semibold">{card.value}</p>
                <p className="mt-1 text-xs opacity-75">{card.detail}</p>
              </div>
            );
          })}
        </div>
      ) : null}

      {trafficCards.length > 0 ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            {trafficCards.map((card, index) => {
              const Icon = TRAFFIC_CARD_ICONS[index] ?? Activity;
              return (
                <div key={card.label} className={`rounded-lg border p-3 ${monitoringToneClass[card.tone]}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium">{card.label}</p>
                    <Icon className="h-4 w-4 shrink-0 opacity-70" />
                  </div>
                  <p className="mt-2 text-2xl font-semibold">{card.value}</p>
                  <p className="mt-1 text-xs opacity-75">{card.detail}</p>
                </div>
              );
            })}
          </div>
          <SajugTrafficChart rows={trafficRows} bucketMinutes={bucketMinutes} />
        </div>
      ) : (
        <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
          시간대별 동접 히스토리가 아직 없습니다.
        </div>
      )}

      <div className="overflow-hidden rounded-lg border">
        <Table mobileCards>
          <TableHeader>
            <TableRow>
              <TableHead>라우트</TableHead>
              <TableHead className="text-right">처리 중</TableHead>
              <TableHead className="text-right">요청</TableHead>
              <TableHead className="text-right">오류율</TableHead>
              <TableHead className="text-right">p95</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {routes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                  아직 라우트별 요청 데이터가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              routes.map((route) => (
                <TableRow key={`${route.method}-${route.path}`}>
                  <TableCell className="max-w-sm break-all">
                    <span className="mr-2 rounded border px-1.5 py-0.5 text-xs font-semibold">{route.method}</span>
                    {route.path}
                  </TableCell>
                  <TableCell className="text-right">{formatMetricNumber(route.active)}</TableCell>
                  <TableCell className="text-right">{formatMetricNumber(route.total)}</TableCell>
                  <TableCell className="text-right">{formatMetricPercent(route.errorRate)}</TableCell>
                  <TableCell className="text-right">{formatMetricNumber(route.latencyMs.p95)}ms</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function buildSajugErrorRouteSummary(rows: SajugRouteDailyError[]) {
  const grouped = new Map<string, {
    method: string;
    path: string;
    total: number;
    errors: number;
    lastDate: string;
    latencyWeightedTotal: number;
  }>();

  rows.forEach((row) => {
    const key = `${row.method} ${row.path}`;
    const current = grouped.get(key) ?? {
      method: row.method,
      path: row.path,
      total: 0,
      errors: 0,
      lastDate: row.date,
      latencyWeightedTotal: 0,
    };
    current.total += row.total;
    current.errors += row.errors;
    current.lastDate = current.lastDate > row.date ? current.lastDate : row.date;
    current.latencyWeightedTotal += row.avgLatencyMs * row.total;
    grouped.set(key, current);
  });

  return Array.from(grouped.values())
    .filter((row) => row.errors > 0)
    .map((row) => ({
      ...row,
      errorRate: row.total > 0 ? (row.errors / row.total) * 100 : 0,
      avgLatencyMs: row.total > 0 ? row.latencyWeightedTotal / row.total : 0,
    }))
    .sort((left, right) => right.errors - left.errors || right.errorRate - left.errorRate)
    .slice(0, 10);
}

function SajugErrorDetails({ response }: { response: SajugErrorHistoryResponse | null }) {
  const errorEvents = response?.errorEvents ?? [];
  const routeSummary = buildSajugErrorRouteSummary(response?.routeDaily ?? []);

  return (
    <div className="space-y-3 border-t pt-5">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-base font-semibold">최근 오류 상세</h3>
          <p className="text-sm text-muted-foreground">
            오류율 숫자를 발생 API와 서버 원인 메시지까지 연결해 보여줍니다.
          </p>
        </div>
        {response ? (
          <span className="text-xs text-muted-foreground">최근 {formatMetricNumber(response.windowDays)}일</span>
        ) : null}
      </div>

      {!response ? (
        <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
          오류 상세 데이터를 불러오지 못했습니다.
        </div>
      ) : errorEvents.length === 0 ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 text-sm text-emerald-900">
          조회 기간에 저장된 서버 오류가 없습니다.
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
          <div className="space-y-2">
            <p className="text-sm font-medium">최근 발생</p>
            <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1">
              {errorEvents.slice(0, 20).map((event, index) => (
                <div
                  key={`${event.occurredAt}-${event.method}-${event.path}-${index}`}
                  className="rounded-lg border border-destructive/20 bg-destructive/[0.03] p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="destructive">{event.statusCode ?? '5xx'}</Badge>
                    <span className="rounded border px-1.5 py-0.5 text-xs font-semibold">{event.method}</span>
                    <span className="min-w-0 break-all text-sm font-medium">{event.path}</span>
                  </div>
                  <p className="mt-2 break-words text-sm text-foreground">
                    {event.detail || '상세 원인 저장 기능 적용 이전에 발생한 오류입니다.'}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {formatMetricTimestamp(event.occurredAtIso)}
                    {typeof event.latencyMs === 'number' ? ` · ${formatMetricNumber(Math.round(event.latencyMs))}ms` : ''}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">수정 우선순위</p>
            <div className="overflow-hidden rounded-lg border">
              <Table mobileCards>
                <TableHeader className="bg-muted/60">
                  <TableRow>
                    <TableHead>API</TableHead>
                    <TableHead className="text-right">오류</TableHead>
                    <TableHead className="text-right">오류율</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {routeSummary.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="py-6 text-center text-sm text-muted-foreground">
                        경로별 오류 집계가 아직 없습니다.
                      </TableCell>
                    </TableRow>
                  ) : (
                    routeSummary.map((route) => (
                      <TableRow key={`${route.method}-${route.path}`}>
                        <TableCell className="max-w-[260px]">
                          <span className="mr-1.5 rounded border px-1 py-0.5 text-[10px] font-semibold">{route.method}</span>
                          <span className="break-all text-xs">{route.path}</span>
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            최근 {route.lastDate} · 평균 {formatMetricNumber(Math.round(route.avgLatencyMs))}ms
                          </p>
                        </TableCell>
                        <TableCell className="text-right font-medium">{formatMetricNumber(route.errors)}회</TableCell>
                        <TableCell className="text-right">{formatMetricPercent(route.errorRate)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SajugTrafficHourlyTable({ rows }: { rows: SajugTrafficChartRow[] }) {
  return (
    <div className="overflow-hidden rounded-lg border">
      <Table mobileCards className="min-w-[960px]">
        <TableHeader className="bg-muted/60">
          <TableRow>
            <TableHead>시간</TableHead>
            <TableHead className="text-right">피크 동접</TableHead>
            <TableHead className="text-right">로그인 / 익명</TableHead>
            <TableHead className="text-right">요청</TableHead>
            <TableHead className="text-right">오류</TableHead>
            <TableHead className="text-right">오류율</TableHead>
            <TableHead className="text-right">평균 응답</TableHead>
            <TableHead className="text-right">처리중 피크</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                표시할 시간대별 데이터가 없습니다.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.bucketStart}>
                <TableCell className="font-medium">{`${row.bucketStart.slice(5, 10)} ${row.label}`}</TableCell>
                <TableCell className="text-right">{formatTrafficMetricValue(row.activeUsersPeak, 'users')}</TableCell>
                <TableCell className="text-right text-muted-foreground">
                  {formatMetricNumber(row.authenticatedPeak)} / {formatMetricNumber(row.anonymousPeak)}
                </TableCell>
                <TableCell className="text-right">{formatTrafficMetricValue(row.requests, 'requests')}</TableCell>
                <TableCell className="text-right">{formatTrafficMetricValue(row.errors, 'errors')}</TableCell>
                <TableCell className="text-right">{formatTrafficMetricValue(row.errorRate, 'percent')}</TableCell>
                <TableCell className="text-right">{formatTrafficMetricValue(row.avgLatencyMs, 'latency')}</TableCell>
                <TableCell className="text-right">{formatTrafficMetricValue(row.activeRequestsPeak, 'activeRequests')}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function SajugTrafficChart({ rows, bucketMinutes }: { rows: SajugTrafficChartRow[]; bucketMinutes: number }) {
  const visibleRows = rows.filter((row) => row.requests > 0 || row.activeUsersPeak > 0);
  const chartRows = visibleRows.length > 0 ? visibleRows : rows;
  const gridTemplateColumns = `repeat(${Math.max(chartRows.length, 1)}, minmax(28px, 1fr))`;

  return (
    <div className="rounded-lg border p-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-semibold">시간대별 동접 피크</p>
          <p className="mt-1 text-xs text-muted-foreground">
            최근 24시간 · {bucketMinutes}분 단위 · 진한 막대는 동접, 얇은 막대는 요청량입니다.
          </p>
        </div>
        <div className="flex gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm bg-primary" />
            동접
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm bg-slate-300" />
            요청
          </span>
        </div>
      </div>
      {chartRows.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">표시할 트래픽 히스토리가 없습니다.</p>
      ) : (
        <div className="mt-4 overflow-x-auto pb-2">
          <div className="min-w-[720px]">
            <div className="grid h-48 items-end gap-2 border-b" style={{ gridTemplateColumns }}>
              {chartRows.map((row, index) => (
                <div
                  key={row.bucketStart}
                  className="flex h-full min-w-0 flex-col justify-end gap-1"
                  title={`${row.label} · 동접 ${row.activeUsersPeak}명 · 요청 ${row.requests}회 · 오류율 ${row.errorRate}%`}
                >
                  <div className="relative flex h-40 items-end justify-center rounded-t bg-muted/50 px-1">
                    <div
                      className={`w-full max-w-8 rounded-t transition-colors ${
                        row.isPeak ? 'bg-primary' : 'bg-primary/70'
                      }`}
                      style={{ height: `${row.activeHeightPercent}%` }}
                    />
                    <div
                      className="absolute bottom-0 left-1/2 w-1.5 -translate-x-1/2 rounded-t bg-slate-300"
                      style={{ height: `${row.requestHeightPercent}%` }}
                    />
                  </div>
                  <div className="h-5 text-center text-[10px] leading-5 text-muted-foreground">
                    {index % 3 === 0 || row.isPeak ? row.label : ''}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 grid gap-2 text-xs text-muted-foreground" style={{ gridTemplateColumns }}>
              {chartRows.map((row) => (
                <div key={`${row.bucketStart}-value`} className="min-w-0 text-center">
                  {row.isPeak ? (
                    <span className="rounded-full bg-primary/10 px-2 py-1 font-medium text-primary">
                      {formatMetricNumber(row.activeUsersPeak)}명
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlatformDailyMetricsTable({
  rows,
  view,
}: {
  rows: PlatformDailyMetricRow[];
  view: PlatformDailyMetricsView;
}) {
  const tableRows = [...rows].reverse();

  return (
    <div className="overflow-hidden rounded-lg border">
      <Table mobileCards className="min-w-[980px] border-collapse">
        <TableHeader className="bg-muted/60">
          <TableRow className="[&>th]:border-r [&>th:last-child]:border-r-0">
            <TableHead className="sticky left-0 z-10 bg-muted/95">일자</TableHead>
            {(view === 'all' || view === 'web') ? (
              <>
                <TableHead className="text-right">웹 앱 열기</TableHead>
                <TableHead className="text-right">웹 결과 조회</TableHead>
                <TableHead className="text-right">웹 로그인</TableHead>
                <TableHead className="text-right">웹 평균 체류</TableHead>
              </>
            ) : null}
            {(view === 'all' || view === 'mobile') ? (
              <>
                <TableHead className="text-right">모바일 앱 열기</TableHead>
                <TableHead className="text-right">모바일 결과 조회</TableHead>
                <TableHead className="text-right">모바일 로그인</TableHead>
                <TableHead className="text-right">모바일 평균 체류</TableHead>
              </>
            ) : null}
            {(view === 'all' || view === 'unknown') ? (
              <>
                <TableHead className="text-right">미분류 앱 열기</TableHead>
                <TableHead className="text-right">미분류 결과 조회</TableHead>
                <TableHead className="text-right">미분류 로그인</TableHead>
                <TableHead className="text-right">미분류 평균 체류</TableHead>
              </>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {tableRows.map((row) => (
            <TableRow key={row.date} className="[&>td]:border-r [&>td:last-child]:border-r-0">
              <TableCell className="sticky left-0 z-10 bg-background font-medium">{row.date}</TableCell>
              {(view === 'all' || view === 'web') ? (
                <>
                  <TableCell><DailyMetricTableValue cell={row.web.appOpened} kind="count" /></TableCell>
                  <TableCell><DailyMetricTableValue cell={row.web.resultViewed} kind="count" /></TableCell>
                  <TableCell><DailyMetricTableValue cell={row.web.loginCompleted} kind="count" /></TableCell>
                  <TableCell><DailyMetricTableValue cell={row.web.dwellAverageSeconds} kind="duration" goodDirection="neutral" /></TableCell>
                </>
              ) : null}
              {(view === 'all' || view === 'mobile') ? (
                <>
                  <TableCell><DailyMetricTableValue cell={row.mobile.appOpened} kind="count" /></TableCell>
                  <TableCell><DailyMetricTableValue cell={row.mobile.resultViewed} kind="count" /></TableCell>
                  <TableCell><DailyMetricTableValue cell={row.mobile.loginCompleted} kind="count" /></TableCell>
                  <TableCell><DailyMetricTableValue cell={row.mobile.dwellAverageSeconds} kind="duration" goodDirection="neutral" /></TableCell>
                </>
              ) : null}
              {(view === 'all' || view === 'unknown') ? (
                <>
                  <TableCell><DailyMetricTableValue cell={row.unknown.appOpened} kind="count" /></TableCell>
                  <TableCell><DailyMetricTableValue cell={row.unknown.resultViewed} kind="count" /></TableCell>
                  <TableCell><DailyMetricTableValue cell={row.unknown.loginCompleted} kind="count" /></TableCell>
                  <TableCell><DailyMetricTableValue cell={row.unknown.dwellAverageSeconds} kind="duration" goodDirection="neutral" /></TableCell>
                </>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
